/**
 * As novidades de uma actualização do painel e a ordem em que o painel se apresenta sozinho:
 *
 * 1. as **novidades** (esta janela, em diapositivos), a quem já usava o painel antes delas;
 * 2. o **tutorial** do painel (o guia dos sítios, `tutorialDoPainel`), a seguir;
 *
 * cada um uma única vez — o servidor guarda que foi visto (`ApresentacaoDoPainel`) e diz o que
 * está por ver na prop `apresentacao`. Quem já viu as novidades só passa pelo tutorial, se o tiver
 * por ver. As duas voltam a pedido no menu da conta («Novidades», «Ver o tutorial»).
 */
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Botao } from "../base";
import { Janela } from "../sobreposicoes";

/** Um diapositivo das novidades. */
export interface DiapositivoNovidade {
    chave: string;
    /** O ícone em grande, no quadrado da cor do painel. */
    icone: ReactNode;
    titulo: string;
    texto: ReactNode;
    /** Uma linha de destaque por baixo do texto (um atalho, onde fica). */
    destaque?: ReactNode;
}

/** O que está por ver (a prop `apresentacao` do painel). */
export interface ApresentacaoPendente {
    novidades: boolean;
    tutorial: boolean;
}

export function JanelaNovidades({ aberta, onFechar, produto, artigo = "o", diapositivos }: {
    aberta: boolean;
    /** Corre ao fechar, seja por «Começar», por «Saltar» ou pelo Esc. */
    onFechar: () => void;
    /** «Logigate», «Escola Inteligente». */
    produto: string;
    /** «o» Logigate, «a» Escola Inteligente: «Novidades do…» / «Novidades da…». */
    artigo?: "o" | "a";
    diapositivos: DiapositivoNovidade[];
}) {
    const [indice, setIndice] = useState(0);

    useEffect(() => {
        if (aberta) {
            setIndice(0);
        }
    }, [aberta]);

    const total = diapositivos.length;
    const actual = diapositivos[Math.min(indice, total - 1)];
    const ultimo = indice >= total - 1;

    if (!actual) {
        return null;
    }

    return (
        <Janela
            aberta={aberta}
            onFechar={onFechar}
            titulo={`Novidades d${artigo} ${produto}`}
            descricao="O que mudou nesta actualização. Leva menos de um minuto."
            confirmarAoFechar={false}
            fecharAoClicarFora={false}
            rodape={
                <div className="flex w-full items-center gap-2">
                    {!ultimo && (
                        <Botao variante="fantasma" onClick={onFechar}>
                            Saltar
                        </Botao>
                    )}
                    <span className="flex-1" />
                    {indice > 0 && (
                        <Botao variante="neutro" icone={<ArrowLeft className="size-4" />} onClick={() => setIndice((i) => Math.max(0, i - 1))}>
                            Anterior
                        </Botao>
                    )}
                    <Botao iconeDireita={ultimo ? undefined : <ArrowRight className="size-4" />} onClick={() => (ultimo ? onFechar() : setIndice((i) => i + 1))}>
                        {ultimo ? "Começar a usar" : "Seguinte"}
                    </Botao>
                </div>
            }
        >
            <div data-novidades data-novidade={actual.chave} className="flex flex-col items-center px-1 pb-1 pt-2 text-center sm:px-4">
                <div key={actual.chave} className="flex size-16 items-center justify-center rounded-2xl bg-portal-suave text-portal-texto ring-1 ring-inset ring-portal/15 animate-in fade-in-0 zoom-in-95 duration-300 dark:bg-portal-claro/10 dark:text-portal-claro dark:ring-portal-claro/20 [&_svg]:size-8">
                    {actual.icone}
                </div>
                <p className="mt-4 text-xs font-medium uppercase tracking-wider text-zinc-400" aria-live="polite">
                    {indice + 1} de {total}
                </p>
                <h3 className="mt-1 text-lg font-semibold text-zinc-900 dark:text-zinc-50">{actual.titulo}</h3>
                <div className="mt-2 max-w-md text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{actual.texto}</div>
                {actual.destaque && (
                    <div className="mt-4 rounded-lg bg-zinc-50 px-3 py-2 text-[13px] text-zinc-700 ring-1 ring-inset ring-zinc-200 dark:bg-white/5 dark:text-zinc-300 dark:ring-white/10">{actual.destaque}</div>
                )}

                <div className="mt-6 flex items-center gap-1.5" role="group" aria-label="Novidades">
                    {diapositivos.map((d, i) => (
                        <button
                            key={d.chave}
                            type="button"
                            aria-label={`${i + 1}: ${d.titulo}`}
                            aria-current={i === indice ? "step" : undefined}
                            onClick={() => setIndice(i)}
                            // A área de toque tem 24 px; o ponto desenhado é mais pequeno.
                            className="group flex size-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-portal/40"
                        >
                            <span className={cn("block h-1.5 rounded-full transition-all", i === indice ? "w-5 bg-portal dark:bg-portal-claro" : "w-1.5 bg-zinc-300 group-hover:bg-zinc-400 dark:bg-zinc-600")} />
                        </button>
                    ))}
                </div>
            </div>
        </Janela>
    );
}

/**
 * A ordem da apresentação: as novidades (se estiverem por ver), depois o tutorial (se estiver por
 * ver). Lê o que está pendente só ao montar o casco — o casco fica montado entre páginas, por isso
 * isto corre uma vez por carregamento. `desligada` adia tudo (ex.: o painel tapado pelo bloqueio do
 * plano).
 */
export function useApresentacaoDoPainel({ pendentes, marcarVisto, abrirTutorial, desligada = false }: {
    pendentes?: ApresentacaoPendente | null;
    marcarVisto: (qual: "novidades" | "tutorial") => void;
    /** Abre o tutorial (o ficheiro com o driver.js carrega-se aqui); `aoTerminar` corre quando fecha. */
    abrirTutorial: (aoTerminar: () => void) => void;
    desligada?: boolean;
}) {
    const [novidadesAbertas, setNovidadesAbertas] = useState(false);
    const novidadesPorVer = useRef(Boolean(pendentes?.novidades));
    const tutorialPorVer = useRef(Boolean(pendentes?.tutorial));

    const verTutorial = () => abrirTutorial(() => marcarVisto("tutorial"));

    const tutorialSePendente = () => {
        if (!tutorialPorVer.current) {
            return;
        }
        tutorialPorVer.current = false;
        verTutorial();
    };

    useEffect(() => {
        if (desligada || (!novidadesPorVer.current && !tutorialPorVer.current)) {
            return;
        }
        // Um momento depois de a página aparecer: o tutorial precisa do casco desenhado.
        const t = window.setTimeout(() => {
            if (novidadesPorVer.current) {
                setNovidadesAbertas(true);
            } else {
                tutorialSePendente();
            }
        }, 700);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [desligada]);

    const fecharNovidades = () => {
        setNovidadesAbertas(false);
        if (novidadesPorVer.current) {
            novidadesPorVer.current = false;
            marcarVisto("novidades");
            // Depois de a janela acabar de sair (a saída anima ~160 ms).
            window.setTimeout(tutorialSePendente, 350);
        }
    };

    return {
        novidadesAbertas,
        abrirNovidades: () => setNovidadesAbertas(true),
        fecharNovidades,
        verTutorial,
    };
}
